import { useEffect, useRef, useState } from "react";
import {
  LineStyle,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { resolveCssVar } from "../../utils/cssColor";
import type { Theme } from "../../theme/useTheme";
import { formatPnl, formatTime, formatTradeId } from "../../utils/format";

// Same UTC->MSK tick/crosshair formatting PriceChart.tsx already needs —
// see its own comment for why this can't just be lightweight-charts'
// default formatter.
function timeToDate(time: Time): Date {
  return new Date((time as UTCTimestamp) * 1000);
}

function crosshairTimeFormatter(time: Time): string {
  return formatTime(timeToDate(time).toISOString(), true);
}

export interface PnlBarPoint {
  time: UTCTimestamp; // trade's close time
  tradeId: string;
  pnl: number; // signed, USDC
}

interface PnlChartProps {
  data: PnlBarPoint[];
  theme: Theme;
  height?: number;
  onMarkerClick?: (tradeId: string) => void;
  // Same purpose as PriceChart's onChartApi — Overview.tsx wires the two
  // charts' timeScales together with this, so panning/zooming the price
  // chart above moves this one in lockstep (design reference: "P&L по
  // сделкам" stacked directly under the price chart, sharing its x-axis).
  onChartApi?: (chart: IChartApi) => void;
  // See PriceChart's own disablePan prop comment — same reasoning here:
  // this chart's own range is always driven by the price chart above it
  // (see the "Deliberately no fitContent()" comment below), never by
  // independent user panning, so its own wheel/drag/pinch handlers would
  // only ever fight that sync rather than do anything useful.
  disablePan?: boolean;
}

/**
 * Per-trade P&L bars + a running cumulative line, replacing the mockup's
 * hand-rolled SVG (deploy/dashboard/web/design/…v2.dc.html's "P&L по
 * сделкам" panel) with a real lightweight-charts pane: one bar per CLOSED
 * trade at its close time (green above the zero line, red below — sign of
 * P&L, not LONG/SHORT), plus a dashed line for the cumulative running
 * total within whatever's currently in view. Same imperative-chart-calls-
 * in-one-effect shape as PriceChart.tsx, for the same reason (see its own
 * docstring).
 */
export function PnlChart({ data, theme, height = 140, onMarkerClick, onChartApi, disablePan = false }: PnlChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const barSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const cumSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const dataRef = useRef<PnlBarPoint[]>(data);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onChartApiRef = useRef(onChartApi);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; point: PnlBarPoint } | null>(null);

  dataRef.current = data;
  onMarkerClickRef.current = onMarkerClick;
  onChartApiRef.current = onChartApi;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const fg3 = resolveCssVar("--fg3");
    const grid = resolveCssVar("--grid");
    const line = resolveCssVar("--line");
    const line2 = resolveCssVar("--line2");

    const chart = createChart(container, {
      width: container.clientWidth,
      height,
      layout: {
        background: { color: "transparent" },
        textColor: fg3,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: grid },
        horzLines: { color: "transparent" },
      },
      rightPriceScale: { borderColor: line },
      timeScale: { borderColor: line, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
      localization: { timeFormatter: crosshairTimeFormatter },
      handleScroll: !disablePan,
      handleScale: !disablePan,
    });
    chartRef.current = chart;
    onChartApiRef.current?.(chart);

    // base:0 is the whole point — bars grow up from zero for a profitable
    // trade and down for a loss, no manual pixel math needed (the mockup
    // hand-computes this per bar; the library does it natively).
    const barSeries = chart.addHistogramSeries({
      base: 0,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    barSeriesRef.current = barSeries;
    barSeries.createPriceLine({
      price: 0,
      color: line2,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: false,
    });

    // Cumulative line lives on its own hidden scale — its magnitude (a
    // running total) has nothing to do with individual bar heights, a
    // shared scale would flatten the bars whenever the total run up over
    // many trades.
    const cumSeries = chart.addLineSeries({
      color: fg3,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      priceScaleId: "cum",
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    cumSeriesRef.current = cumSeries;
    chart.priceScale("cum").applyOptions({ visible: false });

    const handleMove = (param: MouseEventParams) => {
      if (!param.time || !param.point) {
        setTooltip(null);
        return;
      }
      const point = dataRef.current.find((d) => d.time === param.time);
      if (!point) {
        setTooltip(null);
        return;
      }
      setTooltip({ x: param.point.x, y: param.point.y, point });
    };
    const handleClick = (param: MouseEventParams) => {
      if (!param.time) return;
      const point = dataRef.current.find((d) => d.time === param.time);
      if (point) onMarkerClickRef.current?.(point.tradeId);
    };
    chart.subscribeCrosshairMove(handleMove);
    chart.subscribeClick(handleClick);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) chart.applyOptions({ width: entry.contentRect.width });
    });
    resizeObserver.observe(container);

    return () => {
      chart.unsubscribeCrosshairMove(handleMove);
      chart.unsubscribeClick(handleClick);
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      barSeriesRef.current = null;
      cumSeriesRef.current = null;
      setTooltip(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, height]);

  useEffect(() => {
    const barSeries = barSeriesRef.current;
    const cumSeries = cumSeriesRef.current;
    if (!barSeries || !cumSeries) return;

    const long = resolveCssVar("--long");
    const short = resolveCssVar("--short");

    const sorted = [...data].sort((a, b) => (a.time as number) - (b.time as number));
    // lightweight-charts requires strictly ascending, UNIQUE times in
    // setData() — two trades closing within the same second (confirmed
    // live: two bots panic-closed a few seconds apart from each other,
    // landing in the same second) collide here, throwing "Value is null"
    // deep inside the histogram series' own internal validation ("при
    // переключении с линии на свечи" was just when it happened to be
    // reported — confirmed live this actually fires on ANY chart
    // recreation, e.g. a plain range switch, not specifically a mode
    // switch). Bars represent discrete TRADE events, not a sampled series,
    // so nudging a collision 1 second later — cascading through any further
    // collisions the nudge itself creates — keeps every trade as its own
    // distinct, independently hoverable bar instead of one silently
    // crashing the chart or the two overwriting each other at the same x.
    // Invisible at any zoom level that would ever show individual bars.
    for (let i = 1; i < sorted.length; i++) {
      if ((sorted[i].time as number) <= (sorted[i - 1].time as number)) {
        sorted[i] = { ...sorted[i], time: ((sorted[i - 1].time as number) + 1) as UTCTimestamp };
      }
    }
    barSeries.setData(sorted.map((d) => ({ time: d.time, value: d.pnl, color: d.pnl >= 0 ? long : short })));
    // handleMove/handleClick below hit-test by exact `param.time` equality
    // against dataRef.current — the CHART now renders `sorted`'s nudged
    // times (see above), not the raw `data` prop's original ones, so
    // dataRef must hold the SAME nudged times or a hover/click on a nudged
    // bar would silently find nothing (no entry in the raw prop shares its
    // now-adjusted time). The plain `dataRef.current = data` assignment
    // below (every render, for freshness before this effect has run on a
    // brand new mount) is intentionally overwritten by this, not redundant
    // with it — effects run after render, so this always wins by the time
    // a user can actually hover/click anything.
    dataRef.current = sorted;

    // Cumulative within the currently loaded window only, starting at 0 —
    // not an attempt to reconstruct the all-time running total (would need
    // every trade before the window, which isn't fetched). Labeled
    // "накопительно" against this window's own net, not the KPI card's
    // all-time Общий P&L.
    let running = 0;
    cumSeries.setData(
      sorted.map((d) => {
        running += d.pnl;
        return { time: d.time, value: running };
      }),
    );

    // Deliberately no fitContent() here — this chart's visible range is
    // always driven by the price chart above it (see Overview.tsx's
    // handleMainChartApi/handlePnlChartApi sync). Fitting independently
    // here raced against that sync: this chart's own "full extent" can
    // differ from the price chart's (fewer trades than price points, or a
    // trade right at the edge of the loaded window), so whichever
    // fitContent() call landed last silently won and the two panes showed
    // different time ranges — confirmed live, the price chart showed a
    // ~1-day slice while this one showed the full multi-month history.
  }, [data]);

  return (
    <div style={{ position: "relative" }}>
      <div ref={containerRef} style={{ width: "100%", height }} />
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            transform: "translate(-50%, -120%)",
            pointerEvents: "none",
            padding: "9px 11px",
            borderRadius: 10,
            background: "var(--surface3)",
            border: "1px solid var(--line2)",
            boxShadow: "var(--pop)",
            minWidth: 170,
            zIndex: 50,
          }}
        >
          <div style={{ font: "400 10.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
            {formatTime(new Date(tooltip.point.time * 1000).toISOString(), true)}
          </div>
          <div
            style={{
              font: "700 16px 'JetBrains Mono',monospace",
              marginTop: 3,
              color: tooltip.point.pnl >= 0 ? "var(--long)" : "var(--short)",
            }}
          >
            {formatPnl(tooltip.point.pnl)}
          </div>
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)", font: "400 10.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
            {formatTradeId(tooltip.point.tradeId)} · клик — детали
          </div>
        </div>
      )}
    </div>
  );
}
